import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    useFetcher,
    useLoaderData,
    useNavigate,
    useSearchParams,
} from 'react-router';
import { DirectoryBrowsePage } from '~/ui-rocco/pages/directory-browse/DirectoryBrowsePage';
import {
    ProjectViewPills,
    useProjectViewModes,
} from '~/ui-rocco/pages/directory-browse/ProjectViewPills';
import { DirectoryIntro } from '~/ui-rocco/pages/directory-browse/DirectoryIntro';
import { DirectoryPillBar } from '~/ui-rocco/pages/directory-browse/DirectoryPillBar';
import { DirectoryBrowseGrid } from '~/ui-rocco/pages/directory-browse/DirectoryBrowseGrid';
import { DetailImageOverlay } from '~/ui-rocco/pages/detail-shared';
import {
    PROJECT_IMAGE_TAGS_ROUTE,
    type ProjectImageTags,
} from '~/routes/remix-api.projects.image-tags';
import { imageSourceFile } from '@modules/images/utils/image-source-file';
import { resizeImageUrl } from '@modules/images/utils/resize-image-url';
import type { ProjectGalleryImage } from '~/routes/remix-api.projects.image-tags';
import { DirectoryFilterSidebar } from '~/ui-rocco/pages/directory-browse/DirectoryFilterSidebar';
import { useDirectoryDisplayColumns } from '~/ui-rocco/pages/directory-browse/directory-display-columns';
import { GetMatchedHost } from '~/modules-rocco/get-matched/GetMatchedHost';
import { useCategoryFilterSort } from '@ui-rocco/hooks/use-category-filter-sort';
import { PROJECT_FILTERS_CONFIG } from '@modules-rocco/projects/config/filter.config';
import { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import {
    ALL_PILL_ID,
    IMAGE_LABEL_VIEWS,
    imageLabelCanonicalPath,
} from '../config/project-browse-views';
import {
    clearAllFacetsUrl,
    toAppliedFacetChips,
    toFilterGroupLinks,
} from '../config/image-label-facets';
import { AppliedFilterPills } from '@rocco/ui/filter-sort/components/AppliedFilterPills';
import { ImageVisibilityButton } from './ImageVisibilityButton';
import type { ImageLabelBrowseLoader } from './image-label-browse-loader.server';
import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProjectTileViewModel } from '@rocco/ui/tile-project/view-models/project';

/**
 * Spaces / Styles browse page. Renders the same shell, intro, pill bar and grid
 * as the /projects browse — only the index behind the grid differs, so the two
 * read as one directory with three ways in (the tab nav above the pills).
 */
/**
 * The grid types its tiles as the whole directory union; this page only ever
 * feeds it project tiles, and only project tiles carry what the preview reads.
 */
/** A tile's photo, as the wall counts sameness when `uniqueImage` is on. */
const tilePhotoKey = (model: TileableModel): string | number =>
    ('images' in model && imageSourceFile(model.images[0]?.src ?? '')) ||
    model.id;

const toProjectTiles = (tiles: TileableModel[]): ProjectTileViewModel[] =>
    tiles.filter(
        (tile): tile is ProjectTileViewModel => 'professional' in tile,
    );

/**
 * Width the viewer asks its photographs at, whether they come from the wall or
 * from the project's own gallery: the wall's tiles are a sixth of the screen
 * and a page crop is a 1x desktop rendition, while the viewer draws across
 * most of it.
 */
const VIEWER_IMAGE_WIDTH = 2400;

export const ProjectImageLabelBrowsePage = () => {
    const data = useLoaderData<ImageLabelBrowseLoader>();
    const viewModes = useProjectViewModes(data.view);
    // Four across — six packed the photographs too tightly to read. Keyed
    // separately from 'projects' so choosing a column count here does not
    // follow the reader over to the captioned /projects grid, which is
    // designed at three.
    const { columns, setColumns } = useDirectoryDisplayColumns(
        'projects-image-label',
        4,
    );
    const filterSortProps = useCategoryFilterSort(
        PROJECT_FILTERS_CONFIG,
        SearchIndexes.Projects,
        data.filterStates,
        data.defaultSort,
    );

    // The active pill's label heads the grid, mirroring "All {Category}
    // Projects" on /projects.
    const sectionTitle = data.selectedLabel ?? data.sectionTitle;
    // Leading "All Spaces" / "All Styles" pill, as the category bars carry.
    const [searchParams] = useSearchParams();
    const leadingGroups = useMemo(
        () =>
            toFilterGroupLinks(
                data.facetGroups,
                searchParams,
                data.rootPath,
                // Styles browses by style, so the room facet opens collapsed
                // there; on Spaces it is the point of the page.
                data.view === 'styles' ? ['roomTypePrimary'] : [],
            ),
        [data.facetGroups, data.rootPath, data.view, searchParams],
    );

    // Applied-facet chips above the wall, as the category bars show. These
    // facets are plain query params outside the shared filter states, so the
    // chips are derived here and handed to the pill bar in place of its
    // default row. Removing a chip drops that value; a selected radio in the
    // sidebar will not.
    const navigate = useNavigate();
    const appliedChips = useMemo(
        () =>
            toAppliedFacetChips(data.facetGroups, searchParams, data.rootPath),
        [data.facetGroups, data.rootPath, searchParams],
    );
    const clearAllTo = useMemo(
        () => clearAllFacetsUrl(data.facetGroups, searchParams, data.rootPath),
        [data.facetGroups, data.rootPath, searchParams],
    );
    const appliedFilters = (
        <AppliedFilterPills
            items={appliedChips.map((chip) => ({
                id: chip.id,
                label: chip.label,
                onRemove: () => navigate(chip.to, { preventScrollReset: true }),
            }))}
            onClearAll={() =>
                navigate(clearAllTo, { preventScrollReset: true })
            }
            theme="dark"
            className="pb-6"
        />
    );

    // Clicking a tile opens the preview rather than following its link, so the
    // reader keeps their place in the wall.
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    // The grid grows past its first page under View More, so the preview reads
    // the tiles the grid handed back on click rather than the loader's page.
    const [loadedTiles, setLoadedTiles] = useState<TileableModel[]>(data.tiles);
    // A pill, facet, search or sort navigation hands back a different wall.
    // Without this the preview kept the previous set and could stay open over
    // results that are no longer on screen.
    useEffect(() => {
        setLoadedTiles(data.tiles);
        setOpenIndex(null);
    }, [data.tiles]);
    const projectTiles = useMemo(
        () => toProjectTiles(loadedTiles),
        [loadedTiles],
    );

    const previewImages = useMemo(
        () =>
            // Index-aligned with the grid: the tile click hands back a position
            // in the rendered set, so nothing may be dropped here.
            projectTiles.map((tile) => ({
                id: tile.id,
                src: tile.images[0]?.src ?? '',
                alt: tile.images[0]?.alt ?? tile.title,
                projectTitle: tile.title,
                professionalName: tile.professional.name,
                professionalLink: tile.professional.link,
                professionalID: tile.professional.id,
                // Null on a surface that did not fetch websites, which is the
                // viewer's cue to leave its bottom-left corner empty.
                website: tile.website ?? null,
                projectLink: tile.link,
                imageLabel: tile.imageLabel,
            })),
        [projectTiles],
    );

    // Tags are fetched per image as the reader opens or pages through, so the
    // grid load stays as light as it was.
    // The overlay carousels one project's images, not the whole wall: opening a
    // tile narrows to the images from that project already in this result set,
    // so the dots count that project rather than the 36-tile page.
    const opened = openIndex === null ? undefined : previewImages[openIndex];
    const projectKey = opened?.projectLink.split('?')[0];
    const projectImages = useMemo(
        () =>
            projectKey
                ? previewImages.filter(
                      (image) =>
                          image.projectLink.split('?')[0] === projectKey &&
                          // A tile the index gave no image for is a blank the
                          // reader would have to page past.
                          !!image.src,
                  )
                : [],
        [previewImages, projectKey],
    );
    const projectIndex = opened
        ? projectImages.findIndex((image) => image.id === opened.id)
        : -1;
    // One request per project, when the viewer opens or moves to another
    // project. The page builder returns every image's markers together, so
    // paging through the carousel needs no further round trips.
    const tagFetcher = useFetcher<{
        link: string;
        tags: ProjectImageTags;
        images?: ProjectGalleryImage[];
    }>();
    const [tagsByProject, setTagsByProject] = useState<
        Record<string, ProjectImageTags>
    >({});
    const [galleryByProject, setGalleryByProject] = useState<
        Record<string, ProjectGalleryImage[]>
    >({});

    useEffect(() => {
        if (!projectKey || tagsByProject[projectKey]) return;
        const params = new URLSearchParams({ link: projectKey });
        tagFetcher.load(`${PROJECT_IMAGE_TAGS_ROUTE}?${params}`);
        // tagFetcher is stable per render pass; re-running on it would loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectKey]);

    useEffect(() => {
        const response = tagFetcher.data;
        // Key by the link the route echoes back, not by whatever project is
        // open now: opening a second project before the first request lands
        // would otherwise file the first response under the second project.
        if (tagFetcher.state !== 'idle' || !response?.link) return;
        setTagsByProject((previous) =>
            previous[response.link]
                ? previous
                : { ...previous, [response.link]: response.tags },
        );
        setGalleryByProject((previous) =>
            previous[response.link]
                ? previous
                : { ...previous, [response.link]: response.images ?? [] },
        );
    }, [tagFetcher.state, tagFetcher.data]);

    const projectTags = projectKey ? tagsByProject[projectKey] : undefined;

    // Once the project's own gallery has come back, the viewer pages through all
    // of it, starting on the photo that was clicked. Until then, or when the
    // photo is not on the project's page, it has the wall's photos from that
    // project, asked for at the viewer's size rather than the wall tile's.
    const gallery = projectKey ? galleryByProject[projectKey] : undefined;
    const openedSource = opened ? imageSourceFile(opened.src) : '';
    const galleryStart =
        gallery?.findIndex((image) => image.source === openedSource) ?? -1;
    const inGallery = galleryStart >= 0;
    const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
    useEffect(() => {
        setGalleryIndex(null);
    }, [openIndex]);

    const viewerSlides =
        inGallery && gallery && opened
            ? gallery.map((image) => {
                  const wallImage = previewImages.find(
                      (tile) => imageSourceFile(tile.src) === image.source,
                  );
                  return {
                      // Asked for at the viewer's size, as the wall's own photos
                      // are: the stored crop is a 1x desktop rendition, and the
                      // viewer draws it across most of the screen.
                      src: resizeImageUrl(image.src, VIEWER_IMAGE_WIDTH),
                      // Pin still wants an id per slide, so keep the gallery's id
                      // when this photo is not on the wall. Visibility actions get
                      // a separate LibraryImageID only when a wall tile matches;
                      // page-builder image IDs are not valid for that mutation.
                      id: wallImage?.id ?? image.id,
                      libraryImageID: wallImage?.id,
                      // Each photograph as the project names it; the project's own
                      // title where it names none, which is at least true of every
                      // picture in the gallery.
                      alt: image.alt ?? opened.projectTitle,
                      credit: {
                          title: opened.projectTitle,
                          titleLink: opened.projectLink,
                          subtitle: opened.professionalName,
                          subtitleLink: opened.professionalLink,
                      },
                      tags: projectTags?.[image.source],
                      href: opened.projectLink,
                  };
              })
            : projectImages.map((image) => ({
                  src: resizeImageUrl(image.src, VIEWER_IMAGE_WIDTH),
                  id: image.id,
                  libraryImageID: image.id,
                  alt: image.alt,
                  credit: {
                      title: image.projectTitle,
                      titleLink: image.projectLink,
                      subtitle: image.professionalName,
                      subtitleLink: image.professionalLink,
                  },
                  tags: projectTags?.[imageSourceFile(image.src)],
                  href: image.projectLink,
              }));
    const viewerIndex = inGallery
        ? (galleryIndex ?? galleryStart)
        : projectIndex;
    const onViewerIndexChange = (index: number) => {
        if (inGallery) {
            setGalleryIndex(index);
            return;
        }
        const target = projectImages[index];
        if (!target) return;
        setOpenIndex(
            previewImages.findIndex((image) => image.id === target.id),
        );
    };

    const closePreview = useCallback(() => setOpenIndex(null), []);

    // Staff hide and restore from the viewer. The wall's own tiles carry the
    // state they were loaded with; a change made here is kept by image id so
    // the button flips without reloading the wall under the open viewer.
    const [hiddenByImage, setHiddenByImage] = useState<Record<number, boolean>>(
        {},
    );
    // The backend changes every LibraryImage that shares the stored file, so
    // every tile for that file flips together, not just the one on screen.
    const onVisibilityChanged = useCallback(
        (libraryImageID: number, hidden: boolean) => {
            const changed = previewImages.find(
                (image) => image.id === libraryImageID,
            );
            const file = changed ? imageSourceFile(changed.src) : '';
            const sharing = previewImages
                .filter((image) => file && imageSourceFile(image.src) === file)
                .map((image) => image.id);
            setHiddenByImage((previous) => ({
                ...previous,
                ...Object.fromEntries(
                    [libraryImageID, ...sharing].map((id) => [id, hidden]),
                ),
            }));
        },
        [previewImages],
    );
    const renderVisibilityAction = (slide: { libraryImageID?: number }) => {
        if (!data.canModerate || !slide.libraryImageID) return null;
        const hidden =
            hiddenByImage[slide.libraryImageID] ??
            previewImages.find((image) => image.id === slide.libraryImageID)
                ?.imageLabel?.hidden ??
            false;
        return (
            <ImageVisibilityButton
                key={slide.libraryImageID}
                libraryImageID={slide.libraryImageID}
                hidden={hidden}
                onChanged={onVisibilityChanged}
            />
        );
    };

    // Figma labels the leading pill just "All" (5762:114549), not
    // "All Spaces" — the tab above already names the view.
    // "All" clears the pill axis and nothing else: the search term and the
    // panel facets are the reader's, and a pill is not the control that drops
    // them.
    const allPillLink = useMemo(() => {
        const params = new URLSearchParams(searchParams);
        params.delete(data.pillFacetKey);
        params.delete('page');
        const query = params.toString();
        return query ? `${data.rootPath}?${query}` : data.rootPath;
    }, [searchParams, data.pillFacetKey, data.rootPath]);

    // The pill links the view's root, as search engines index it; a click
    // keeps the reader's other filters (allPillLink).
    const categoryPills = [
        {
            id: ALL_PILL_ID,
            title: 'All',
            link: data.rootPath,
            ...(allPillLink === data.rootPath
                ? {}
                : { navigateLink: allPillLink }),
        },
        ...data.pills,
    ];

    return (
        <>
            <GetMatchedHost
                enquiryType="professional"
                styleImages={data.getMatchedStyleImages}
            />
            <DirectoryBrowsePage
                entityType="projects"
                theme="dark"
                overlayOpen={projectIndex >= 0}
                intro={
                    <DirectoryIntro
                        eyebrow={data.eyebrow}
                        heading={data.heading}
                        categoryPrefix={data.selectedLabel ?? undefined}
                        entryPoint="CategoryLead"
                        explainer={{
                            eyebrow: 'Get Matched',
                            title: 'Get Matched with the right professionals',
                            body: 'Seen a project you like? Give us a short brief on yours: where it is, what it is, who you want to work with, your budget and timeline. Our team reads it and personally matches you with two or three professionals whose work, availability and approach fit.',
                        }}
                    />
                }
                pillBar={
                    filterSortProps?.searchFilters ? (
                        <DirectoryPillBar
                            viewSwitch={
                                <ProjectViewPills
                                    activeView={data.view}
                                    isDark
                                />
                            }
                            viewModes={viewModes}
                            breadcrumbs={data.breadcrumbs}
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            prefetchUrl={filterSortProps.settledDestinationURL}
                            sectionTitle={sectionTitle}
                            categoryPills={categoryPills}
                            activePillId={data.activePillId}
                            variant="imageBrowse"
                            rootPath={data.rootPath}
                            rootLabel={data.rootLabel}
                            displayColumns={columns}
                            onDisplayColumnsChange={setColumns}
                            appliedFilters={appliedFilters}
                        />
                    ) : null
                }
                sidebar={
                    filterSortProps?.searchFilters &&
                    leadingGroups.length > 0 ? (
                        <DirectoryFilterSidebar
                            searchFilters={filterSortProps.searchFilters}
                            sortOptions={filterSortProps.sortOptions}
                            leadingGroups={leadingGroups}
                            // The project index's own facets do not apply to an
                            // image browse, so hide every config section and let
                            // the ImageLabel groups stand alone.
                            hiddenSectionTitles={PROJECT_FILTERS_CONFIG.modalFilters.map(
                                (section) => section.title,
                            )}
                        />
                    ) : null
                }
                rows={
                    <DirectoryBrowseGrid
                        // Same rule as the meta's canonical: a value no pill
                        // offers pages under the view's root, never as its own
                        // crawlable copy.
                        crawlBase={
                            data.selectedLabel
                                ? imageLabelCanonicalPath(
                                      data.view,
                                      searchParams,
                                  )
                                : IMAGE_LABEL_VIEWS[data.view].path
                        }
                        entityType="projects"
                        preset="imageWall"
                        columns={columns}
                        entityNoun="images"
                        aspectRatio="imageLabel"
                        // One tile per photo across the whole wall, not just
                        // within a page: the same photo is indexed once per
                        // label run, and those rows can land pages apart.
                        dedupeKey={data.uniqueImage ? tilePhotoKey : undefined}
                        onTileClick={(index, tiles) => {
                            // The grid memoises this array, so an unchanged set
                            // is the same reference and React skips the work.
                            setLoadedTiles(tiles);
                            setOpenIndex(index);
                        }}
                        categoryLink={data.rootPath}
                        initialTiles={data.tiles}
                        totalCount={data.totalCount}
                        offsetBase={data.offsetBase}
                        filters={data.filters}
                        sortBy={data.sortBy ?? undefined}
                        firstLoadedPage={data.page}
                        requestedPage={data.page}
                        activeCategoryLabel={data.selectedLabel ?? undefined}
                    />
                }
            />
            {/* The detail pages' viewer, not a second one of our own: the
                same component means the wall's photographs open exactly the
                way a project's or a product's do. */}
            {projectIndex >= 0 && opened ? (
                <DetailImageOverlay
                    slides={viewerSlides}
                    index={viewerIndex}
                    onIndexChange={onViewerIndexChange}
                    onClose={closePreview}
                    // The wall is photographs, and a tile's id is the
                    // LibraryImage behind one rather than the project page —
                    // so a save pins the picture. Saved as a page pin the id
                    // matches no page, and the save writes nothing while
                    // still reporting success.
                    pinId={opened.id}
                    pinType="PhotoPin"
                    title={opened.projectTitle}
                    visitWebsite={
                        opened.website
                            ? {
                                  website: opened.website,
                                  libraryItemID: opened.id,
                                  professionalID: opened.professionalID,
                                  professionalTitle: opened.professionalName,
                              }
                            : null
                    }
                    renderCornerAction={renderVisibilityAction}
                />
            ) : null}
        </>
    );
};

export default ProjectImageLabelBrowsePage;
